// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useBankUploadSectionMeta } from '@client/banks/hooks/use-bank-upload-section-meta';
import { useInstitutionForm } from '@client/banks/hooks/use-institution-form';
import type { BankInstitution } from '@client/banks/types/institution';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback } from 'react';

export const useInstitutionManual = () => {
  const { navigate } = useStepsData();
  const { getFormData, saveFormData } = useBankUploadData();
  const { markSectionCompleted } = useBankUploadSectionMeta();

  const savedInstitution = getFormData('institution');

  const form = useInstitutionForm({
    variant: 'create',
    defaultValues: savedInstitution,
  });

  const isFormValid = useCallback(
    () => form.formState.isValid,
    [form.formState.isValid],
  );

  const onSubmit = useCallback(
    async (data: BankInstitution) => {
      saveFormData('institution', data);
      markSectionCompleted('institution');
      navigate('accounts-list');
    },
    [markSectionCompleted, navigate, saveFormData],
  );

  const onSelectExisting = useCallback(() => {
    navigate('institution-select-existing');
  }, [navigate]);

  return {
    form,
    isFormValid,
    onSubmit,
    onSelectExisting,
  };
};
